import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import BackButton from "@/components/BackButton";
import { toast } from "sonner";
import { Lock, Mail, User, Phone, Flag, AtSign } from "lucide-react";

const emptyReg = { first_name: "", last_name: "", nickname: "", phone: "", email: "", password: "", race_type: "", privacy: false };

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [login_, setLogin_] = useState({ email: "", password: "" });
  const [reg, setReg] = useState(emptyReg);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register" && !reg.race_type) {
      setError("Indica il tipo di gare: RC, SIM o entrambe");
      return;
    }
    if (mode === "register" && !reg.privacy) {
      setError("Devi accettare l'informativa sulla privacy");
      return;
    }
    setLoading(true);
    try {
      let u;
      if (mode === "login") u = await login(login_.email, login_.password);
      else u = await register(reg);
      if (mode === "register") toast.success("Registrazione inviata! In attesa di approvazione.");
      else toast.success("Accesso effettuato");
      navigate(u.role === "admin" ? "/admin" : "/campionati-tuoi");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10 grain overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center opacity-20 blur-sm"
        style={{ backgroundImage: "url(https://images.unsplash.com/photo-1645400379459-f6fd3d963fd4?crop=entropy&cs=srgb&fm=jpg&q=85)" }} />
      <div className="absolute inset-0 bg-zinc-950/80" />
      <div className="relative z-10 w-full max-w-md border border-zinc-800 bg-zinc-900/90 backdrop-blur-xl p-8">
        <BackButton className="mb-6" />
        <Flag className="h-10 w-10 text-yellow-400 text-glow-yellow mb-4" />
        <h1 className="font-heading text-3xl font-black uppercase tracking-tighter text-zinc-50">
          {mode === "login" ? "Accedi" : "Iscriviti"}
        </h1>
        <p className="mt-2 text-sm text-zinc-400">
          {mode === "login" ? "Entra per gestire i tuoi campionati." : "Registrati con i tuoi dati per usare l'app."}
        </p>

        <div className="mt-6 flex gap-2 border-b border-zinc-800">
          {[{ k: "login", l: "Accedi" }, { k: "register", l: "Iscriviti" }].map((t) => (
            <button key={t.k} data-testid={`auth-tab-${t.k}`} onClick={() => { setMode(t.k); setError(""); }}
              className={`px-4 py-2.5 text-xs font-bold tracking-[0.15em] uppercase border-b-2 -mb-px transition-colors duration-200 ${
                mode === t.k ? "border-yellow-400 text-yellow-400" : "border-transparent text-zinc-400 hover:text-zinc-100"
              }`}>
              {t.l}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {mode === "login" ? (
            <>
              <Field icon={Mail} label="Email">
                <input type="email" data-testid="auth-email" value={login_.email} onChange={(e) => setLogin_({ ...login_, email: e.target.value })} required
                  className="w-full bg-transparent py-3 text-zinc-100 outline-none placeholder:text-zinc-600" placeholder="tu@email.com" />
              </Field>
              <Field icon={Lock} label="Password">
                <input type="password" data-testid="auth-password" value={login_.password} onChange={(e) => setLogin_({ ...login_, password: e.target.value })} required
                  className="w-full bg-transparent py-3 text-zinc-100 outline-none placeholder:text-zinc-600" placeholder="••••••••" />
              </Field>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field icon={User} label="Nome">
                  <input data-testid="reg-first" value={reg.first_name} onChange={(e) => setReg({ ...reg, first_name: e.target.value })} required
                    className="w-full bg-transparent py-3 text-zinc-100 outline-none" />
                </Field>
                <Field icon={User} label="Cognome">
                  <input data-testid="reg-last" value={reg.last_name} onChange={(e) => setReg({ ...reg, last_name: e.target.value })} required
                    className="w-full bg-transparent py-3 text-zinc-100 outline-none" />
                </Field>
              </div>
              <Field icon={AtSign} label="Nickname (nome pilota)">
                <input data-testid="reg-nickname" value={reg.nickname} onChange={(e) => setReg({ ...reg, nickname: e.target.value })}
                  className="w-full bg-transparent py-3 text-zinc-100 outline-none" placeholder="es. TuonoRC" />
              </Field>
              <div>
                <label className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400">Tipo di gare</label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {[{ k: "rc", l: "RC" }, { k: "sim", l: "SIM" }, { k: "entrambe", l: "Entrambe" }].map((o) => (
                    <button type="button" key={o.k} data-testid={`reg-race-${o.k}`}
                      onClick={() => setReg({ ...reg, race_type: o.k })}
                      className={`py-3 text-xs font-bold tracking-[0.1em] uppercase border transition-colors duration-200 ${
                        reg.race_type === o.k ? "border-yellow-400 bg-yellow-400/10 text-yellow-400" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                      }`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
              <Field icon={Phone} label="Cellulare">
                <input data-testid="reg-phone" value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} required
                  className="w-full bg-transparent py-3 text-zinc-100 outline-none" placeholder="+39 333 …" />
              </Field>
              <Field icon={Mail} label="Email">
                <input type="email" data-testid="reg-email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} required
                  className="w-full bg-transparent py-3 text-zinc-100 outline-none" placeholder="tu@email.com" />
              </Field>
              <Field icon={Lock} label="Password">
                <input type="password" data-testid="reg-password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} required
                  className="w-full bg-transparent py-3 text-zinc-100 outline-none" placeholder="••••••••" />
              </Field>
              <label className="flex items-start gap-2 text-xs text-zinc-400 cursor-pointer">
                <input type="checkbox" data-testid="reg-privacy" checked={reg.privacy} onChange={(e) => setReg({ ...reg, privacy: e.target.checked })}
                  className="accent-yellow-400 h-4 w-4 mt-0.5" />
                Accetto il trattamento dei miei dati personali e l'informativa sulla privacy.
              </label>
            </>
          )}
          {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
          <button type="submit" data-testid="auth-submit" disabled={loading}
            className="w-full bg-yellow-400 text-zinc-950 font-bold text-sm tracking-[0.15em] uppercase py-4 hover:bg-yellow-300 transition-colors duration-200 disabled:opacity-50">
            {loading ? "Attendere…" : mode === "login" ? "Entra" : "Invia iscrizione"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <label className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400">{label}</label>
      <div className="mt-2 flex items-center gap-2 border border-zinc-700 bg-zinc-950 px-3 focus-within:border-yellow-400 transition-colors duration-200">
        <Icon className="h-4 w-4 text-zinc-500" />
        {children}
      </div>
    </div>
  );
}
